import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { emit } from '../../platform/analytics/analytics'
import { authErrorFor } from '../../auth/errors'
import { AuthFailure } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import { MFA_PATH } from '../../auth/RequireAuth'
import type { FieldError } from '../../auth/errors'
import { AuthCard } from './AuthCard'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * The far end of `sendPasswordReset` — M1's reset flow used to stop at the
 * email, with nothing in the app able to spend the link it sent. Reached at
 * `/reset?mode=resetPassword&oobCode=…`, Firebase's own query shape; only
 * `oobCode` is read.
 *
 * ## What the design draws that this does not build
 *
 * `TrackE5 Web App.dc.html`'s `reset` screen promises three checklist rows
 * under the password field — "at least 12 characters", "not one of your last
 * five passwords", "not in any known breach list" — and a "sign out my other
 * devices" toggle. None of the last three exist anywhere this client can reach:
 *
 *   - **Password history and breach lists have no API**, client or server.
 *     Firebase does not track prior password hashes and nothing here does
 *     either, so both rows would be permanently-lit decoration asserting a
 *     check that never ran.
 *   - **The toggle implies a choice Firebase does not offer.**
 *     `confirmPasswordReset` revokes every refresh token on the account
 *     unconditionally — see `AuthPort.completeReset`. A switch that can be
 *     turned off would be lying about what pressing Save does.
 *   - **The length floor is 6, not 12.** Firebase's own minimum is 6, and
 *     `SignUp.tsx` already tells a new account "At least 6 characters." —
 *     requiring 12 here would refuse a password the same account could have
 *     been created with minutes earlier. A product decision, flagged rather
 *     than made silently.
 *
 * The 4-bar strength meter survives — client-side only, advisory, and not a
 * gate on submission — and so does the confirm field.
 *
 * ## Works signed in or signed out — do not assume either
 *
 * `/reset` is deliberately NOT in `App.tsx`'s `BARE` group, and that is not
 * this file's decision to revisit. `BARE` is wrapped in `RedirectWhenSignedIn`,
 * which would bounce an already-signed-in visitor to `/overview` before they
 * could spend the link — and "I forgot my password on my laptop" is an
 * ordinary way to already be signed in on the browser that receives the email.
 * `/reset` sits beside `/welcome` and `/verify/:token` for the same reason
 * those do.
 *
 * So this component never reads `auth.status` and must not start: there is no
 * `signedOut` branch to add, no assumption that `verifyResetCode` or
 * `completeReset` are reachable only pre-login. The one place the existing
 * session actually matters is the follow-up `auth.signIn` after a successful
 * `completeReset` — it simply replaces whatever Firebase session (if any) the
 * browser already held, exactly as an ordinary sign-in would.
 *
 * ## Screen states — not `Async`
 *
 * Same reasoning as `VerifyDevice`: this screen has no data to be loading,
 * empty or stale, and the two failure arms are not one "something went wrong".
 * `verifying` has to resolve before the form is worth drawing, because
 * discovering a dead link only on submit means the user chose a password and
 * confirmed it before being told the work was wasted. Within that failure,
 * `dead` (expired or already-used — single-use and time-limited by design) is
 * terminal and offers a fresh link rather than a retry that cannot succeed;
 * `unreachable` (a network blip, an unclassified throw) is not, because the
 * SAME code may simply not have arrived yet.
 */

const DEAD_LINK_CODES = new Set(['auth/expired-action-code', 'auth/invalid-action-code'])

type Screen =
  | { status: 'verifying' }
  | { status: 'unreachable' }
  | { status: 'dead' }
  | { status: 'ready'; email: string }
  | { status: 'saving'; email: string }
  /**
   * `signedIn` distinguishes what "Save and sign in" actually managed.
   *
   * `completeReset` succeeding means the password IS changed regardless of
   * what happens next — so a failure of the follow-up `signIn` (a network
   * blip, `auth/invalid-credential` from some Firebase edge case) must not be
   * read as the reset having failed. It changes which copy and which link
   * `done` renders, not whether it renders.
   */
  | { status: 'done'; signedIn: boolean }

/**
 * A rough 0–4 read of how strong a password LOOKS, for the four-bar meter the
 * design draws. Advisory only — it gates nothing, and Firebase's own
 * `auth/weak-password` at submit is the actual floor.
 *
 * Length carries the first two bars, because length is what actually costs an
 * attacker time; the last rewards mixing character classes, which is the one
 * thing a person filling this field can act on immediately without inventing a
 * password Firebase would refuse.
 */
function meterScore(password: string): number {
  let score = password.length >= 6 ? 1 : 0
  if (password.length >= 10) score += 1
  if (password.length >= 14) score += 1
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^\dA-Za-z]/].filter((re) => re.test(password)).length
  if (classes >= 3) score += 1
  return Math.min(score, 4)
}

/** The bar colour and the word beside it — rule 11: colour is never the only signal. */
function meterTier(score: number): { label: string; tier: 'weak' | 'fair' | 'good' } {
  if (score <= 1) return { label: 'Weak', tier: 'weak' }
  if (score === 2) return { label: 'Fair', tier: 'fair' }
  return { label: score === 3 ? 'Good' : 'Strong', tier: 'good' }
}

export function ResetPassword() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const oobCode = params.get('oobCode') ?? ''

  const [screen, setScreen] = useState<Screen>({ status: 'verifying' })
  const [error, setError] = useState<FieldError | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  // The raw password never enters state — only a score derived from it, the
  // same discipline `SignIn`'s comment states for its own password field.
  const [meter, setMeter] = useState({ score: 0, nonEmpty: false })

  const verify = useCallback(async () => {
    if (oobCode === '') {
      // No code at all is the same terminal state as a dead one: there is
      // nothing here to retry, only a fresh link to request.
      setScreen({ status: 'dead' })
      return
    }
    setScreen({ status: 'verifying' })
    try {
      const email = await auth.verifyResetCode(oobCode)
      setScreen({ status: 'ready', email })
    } catch (thrown) {
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setScreen(DEAD_LINK_CODES.has(detail.code) ? { status: 'dead' } : { status: 'unreachable' })
    }
  }, [oobCode, auth])

  useEffect(() => {
    void verify()
    // Keyed on `oobCode` alone. `verify` is rebuilt on every render of this
    // component's context value — the provider hands out a fresh object every
    // time — and re-running the check on every unrelated re-render would spend
    // a second Firebase call for a link that has not changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oobCode])

  function onPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value
    setMeter({ score: meterScore(value), nonEmpty: value.length > 0 })
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>, email: string) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const password = String(form.get('password') ?? '')
    const confirm = String(form.get('confirm') ?? '')

    setError(null)
    setConfirmError(null)

    // Client-only: Firebase never sees the confirm field, so nothing else can
    // catch a mismatch.
    if (password !== confirm) {
      setConfirmError("Those don't match.")
      return
    }

    setScreen({ status: 'saving', email })
    try {
      await auth.completeReset(oobCode, password)
    } catch (thrown) {
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      if (DEAD_LINK_CODES.has(detail.code)) {
        // The link died in the gap between verifying it and submitting — it is
        // single-use and time-limited, and that gap is exactly where it can
        // happen. Same terminal state `verify` reaches, reached from the form.
        setScreen({ status: 'dead' })
        return
      }
      setError(detail)
      setScreen({ status: 'ready', email })
      return
    }

    /**
     * Emitted HERE, between the two calls, and that placement is the point.
     *
     * The password is now changed — `completeReset` resolved — and that fact is
     * independent of whether the follow-up sign-in below succeeds. Reporting it
     * after that call would lose every reset where the session could not be
     * established (a raised second factor, a network blip), which are exactly
     * the resets worth knowing about: the ones where the user got their
     * password back but not their way in.
     *
     * `password_reset_requested` is emitted by `ForgotPassword` at the other
     * end. The pair is what makes the funnel answerable — a request count alone
     * cannot tell a working flow from the dead end this was until today.
     */
    emit('password_reset_completed')

    // The password is set from here on, whatever the sign-in attempt below
    // does with it — see `Screen`'s `done` arm.
    try {
      const outcome = await auth.signIn(email, password)
      if (outcome === 'mfaRequired') {
        // The provider has already raised the challenge in global auth state;
        // `/mfa` is the bare route that answers it. `/reset` carries no MFA UI
        // of its own — RequireAuth's redirect to `/mfa` only guards CHROME
        // routes, and this one is bare, so nothing else would get the user
        // there.
        navigate(MFA_PATH)
        return
      }
      // Mirrors `SignIn`'s own emission: measured here and only here, because
      // this is the one call in this screen that can make it true.
      emit('signin_completed', { method: 'email', mfa_used: false })
      setScreen({ status: 'done', signedIn: true })
    } catch {
      setScreen({ status: 'done', signedIn: false })
    }
  }

  if (screen.status === 'verifying') {
    return (
      <AuthCard title="Checking your link">
        <p className="auth-card__note" role="status">
          One moment…
        </p>
      </AuthCard>
    )
  }

  if (screen.status === 'dead') {
    return (
      <AuthCard title="This link is no longer valid">
        {/* No retry — a dead code cannot be spent twice into working. The
            recovery is a fresh link, which is a new email. */}
        <p className="auth-card__note" role="alert">
          This reset link has expired or has already been used — they're good for one change each.
          Request a new one and we'll email it to you.
        </p>
        <Button variant="primary" size="form" onClick={() => navigate('/forgot-password')}>
          Request a new link
        </Button>
      </AuthCard>
    )
  }

  if (screen.status === 'unreachable') {
    return (
      <AuthCard title="Checking your link">
        <p className="auth-card__note" role="alert">
          We couldn't reach TrackE5 to check that link. It may still be good — check your
          connection and try again.
        </p>
        <Button variant="primary" size="form" onClick={() => void verify()}>
          Try again
        </Button>
      </AuthCard>
    )
  }

  if (screen.status === 'done') {
    return (
      <AuthCard title="Password updated">
        <p className="auth-card__note" role="status">
          {screen.signedIn
            ? "Your password has been changed and you're signed in."
            : 'Your password has been changed. Sign in with your new password.'}
        </p>
        {screen.signedIn ? (
          <Button variant="primary" size="form" onClick={() => navigate('/')}>
            Continue to TrackE5
          </Button>
        ) : (
          <Button variant="primary" size="form" onClick={() => navigate('/signin')}>
            Sign in
          </Button>
        )}
      </AuthCard>
    )
  }

  const busy = screen.status === 'saving'
  const email = screen.email
  const { label: meterLabel, tier: meterTierName } = meterTier(meter.score)

  return (
    <AuthCard
      title="Choose a new password"
      // The design's "Signed in as…" is not true here — nobody is signed in
      // yet, that is the whole point of this screen. Firebase does know whose
      // link this is, and saying so plainly is what that fact is actually good
      // for.
      subtitle={`Resetting the password for ${email}`}
      foot={<InlineLink to="/signin">← Back to sign in</InlineLink>}
    >
      <form className="auth-form" onSubmit={(e) => void onSubmit(e, email)} noValidate>
        <div className="reset-meter-field">
          <Field
            size="form"
            label="New password"
            name="password"
            type="password"
            autoComplete="new-password"
            hint="At least 6 characters."
            onChange={onPasswordChange}
            error={error?.field === 'password' ? error.message : undefined}
          />
          {meter.nonEmpty && (
            <div className="reset-meter-row">
              <div className="reset-meter" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={
                      i < meter.score
                        ? `reset-meter__bar reset-meter__bar--${meterTierName}`
                        : 'reset-meter__bar'
                    }
                  />
                ))}
              </div>
              <span className="reset-meter__label">{meterLabel}</span>
            </div>
          )}
        </div>
        <Field
          size="form"
          label="Confirm"
          name="confirm"
          type="password"
          autoComplete="new-password"
          error={confirmError ?? undefined}
        />
        <FormError error={error} />
        <Button type="submit" variant="primary" size="form" disabled={busy}>
          {busy ? 'Saving…' : 'Save and sign in'}
        </Button>
      </form>
    </AuthCard>
  )
}
